import { useEffect, useRef } from 'react'

/**
 * Vidsrc embed sends play / pause / time / complete events to the parent
 * window via postMessage. The `time` event fires roughly every 5 seconds
 * while the player is running, with `currentTime` and `duration` fields.
 * We listen on the parent window, **track** the latest position locally
 * every ~15 seconds, and **flush** to Firestore at most once per minute
 * (or immediately on player close, tab hide, beforeunload, or completion).
 *
 * Why two cadences instead of one direct write per event:
 *   - Firestore write quota: writing every Vidsrc tick (~5s) over a
 *     2-hour movie costs ~1500 writes. Throttling to 60s drops that to
 *     ~120 — closer to what the desktop's debounced syncMovieUpdate
 *     does, and roughly 1/12 the load.
 *   - Force-flush on close ensures the latest known position lands in
 *     Firestore even if the user bails between flush windows.
 *   - Local track at 15s gives us a recent value to flush on close
 *     without waiting for Vidsrc's next tick.
 *
 * No cloud compute, no CDP debugger — pure browser API. The catch: we
 * can only see what Vidsrc decides to send. If they remove the events
 * tomorrow this hook silently no-ops.
 */
export interface VidsrcProgressEvent {
  /** Current playback position in seconds. */
  currentTime: number
  /** Total runtime in seconds (may be undefined for live/streaming). */
  duration: number | undefined
  /** True if Vidsrc reported the playback finished. */
  completed: boolean
}

interface Options {
  /** Disabled when the player isn't open. */
  enabled: boolean
  /** Local tracking window — at most one onProgress call per (this) ms while the player runs. */
  trackEveryMs?: number
  /** Flush window — at most one persistent write per (this) ms. */
  flushEveryMs?: number
  /** Async writer the caller wants invoked when it's time to persist. */
  onProgress: (event: VidsrcProgressEvent) => Promise<void> | void
}

interface VidsrcMessage {
  type?: string
  event?: string
  name?: string
  action?: string
  // Vidsrc embeds aren't formally documented; we accept several common shapes.
  currentTime?: number
  time?: number
  position?: number
  duration?: number
  totalTime?: number
}

export function useVidsrcProgress({
  enabled,
  trackEveryMs = 15000,
  flushEveryMs = 60000,
  onProgress,
}: Options) {
  // Stash the latest callback in a ref so the listener doesn't have to
  // re-bind on every render.
  const cbRef = useRef(onProgress)
  cbRef.current = onProgress

  // Last known progress event we accepted (after track-throttling).
  const latestRef = useRef<VidsrcProgressEvent | null>(null)
  const lastTrackAtRef = useRef(0)
  const lastFlushAtRef = useRef(0)
  const flushedSinceLastTrackRef = useRef(false)

  useEffect(() => {
    if (!enabled) return

    const flushNow = async () => {
      const ev = latestRef.current
      if (!ev) return
      // Skip if we already flushed this exact value (avoid spurious writes
      // when the user pauses for ages and we beforeunload-flush twice).
      if (flushedSinceLastTrackRef.current) return
      flushedSinceLastTrackRef.current = true
      lastFlushAtRef.current = Date.now()
      try {
        await cbRef.current(ev)
      } catch (err) {
        console.warn('[useVidsrcProgress] flush failed', err)
      }
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as VidsrcMessage | string | null
      if (data == null) return

      let payload: VidsrcMessage | null = null
      if (typeof data === 'string') {
        try {
          payload = JSON.parse(data) as VidsrcMessage
        } catch {
          return
        }
      } else if (typeof data === 'object') {
        payload = data
      }
      if (!payload) return

      const kind = (
        payload.type ||
        payload.event ||
        payload.name ||
        payload.action ||
        ''
      )
        .toString()
        .toLowerCase()

      const isProgress =
        kind === 'time' ||
        kind === 'timeupdate' ||
        kind === 'progress' ||
        kind === 'playerprogress'
      const isComplete =
        kind === 'complete' ||
        kind === 'ended' ||
        kind === 'finished' ||
        kind === 'playerended'

      if (!isProgress && !isComplete) return

      const currentTime =
        payload.currentTime ?? payload.time ?? payload.position ?? null
      const duration = payload.duration ?? payload.totalTime ?? undefined
      if (!isComplete && (currentTime == null || typeof currentTime !== 'number' || currentTime < 0)) {
        return
      }

      // Track-throttle: update the in-memory snapshot at most every trackEveryMs.
      const now = Date.now()
      if (!isComplete && now - lastTrackAtRef.current < trackEveryMs) return
      lastTrackAtRef.current = now

      latestRef.current = {
        currentTime: currentTime ?? 0,
        duration: typeof duration === 'number' ? duration : undefined,
        completed: isComplete,
      }
      flushedSinceLastTrackRef.current = false

      // Completion forces an immediate flush; otherwise honor the
      // longer flush window.
      if (isComplete || now - lastFlushAtRef.current >= flushEveryMs) {
        void flushNow()
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void flushNow()
      }
    }

    const handleBeforeUnload = () => {
      // Synchronous best effort. The async write may not complete, but
      // Firestore's offline buffer will retry on next page load.
      void flushNow()
    }

    window.addEventListener('message', handleMessage)
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)

    return () => {
      // Effect cleanup also runs when the player modal unmounts (e.g.
      // user taps Close), so this is the canonical "save on exit" hook.
      void flushNow()
      window.removeEventListener('message', handleMessage)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handleBeforeUnload)
    }
  }, [enabled, trackEveryMs, flushEveryMs])
}
